/**
 * @lodex-pro/shared/validation/operator-webhooks — operator webhooks PR 1 (migration 0239;
 * lx-coord/operator-webhooks-plan.md, the DEC-242 follow-up).
 *
 * An operator company's webhook endpoints: where its load events are POSTed, which events, and
 * the signing secret (shown once, rotatable). The events themselves are sent from PR 2.
 *
 * Who: the operator admin (`MANAGE_OPERATOR_WEBHOOKS`, OPERATOR_ADMIN only), on a plan with
 * OP_WEBHOOKS (its limit is the number of endpoints).
 *
 * DEC-147: `description` is prose and kept as typed; the URL is kept as typed (paths are
 * case-sensitive) after the server's own checks (https, a public address; see
 * api/src/services/webhook-url-guard.ts).
 */

import { z } from 'zod';
import { upperCaseIdentifier } from './identifier.js';

/**
 * The event catalog (v1). Public names, shared with the Operator API's timeline vocabulary where
 * one exists (PICKED_UP is the API's PICKUP_COMPLETE moment).
 *
 *   ACCEPTED          the hauler assigned the load to a driver (Session 6, 2026-10-19)
 *   EN_ROUTE          the driver started the load
 *   ARRIVED_PICKUP    the driver arrived at the pickup
 *   PICKED_UP         the gauges were saved and the driver left the pickup
 *   COMPLETED         the load is finished (on-site jobs included)
 *   TICKET_AVAILABLE  the ticket is approved and billable
 *   CANCELLED         the load was cancelled
 *   VOIDED            the load's capture was voided (the last event for that load)
 */
export const OPERATOR_WEBHOOK_EVENT_TYPES = [
  'ACCEPTED',
  'EN_ROUTE',
  'ARRIVED_PICKUP',
  'PICKED_UP',
  'COMPLETED',
  'TICKET_AVAILABLE',
  'CANCELLED',
  'VOIDED',
] as const;
export type OperatorWebhookEventType = (typeof OPERATOR_WEBHOOK_EVENT_TYPES)[number];
export const OperatorWebhookEventTypeSchema = z.enum(OPERATOR_WEBHOOK_EVENT_TYPES);

/** The longest URL an endpoint may have. */
export const OPERATOR_WEBHOOK_URL_MAX = 2048;

/** Default overlap when the secret is rotated: the old one keeps signing for a day. */
export const OPERATOR_WEBHOOK_ROTATION_OVERLAP_HOURS_DEFAULT = 24;
/** A week. Beyond this a rotation is two secrets, not a cutover. */
export const OPERATOR_WEBHOOK_ROTATION_OVERLAP_HOURS_MAX = 168;

const url = z.string().trim().min(1).max(OPERATOR_WEBHOOK_URL_MAX);
// DEC-147 widened (Corbin's Q2, 2026-10-27): a description is stored uppercase; only true
// comments, notes and reasons keep their case.
const description = upperCaseIdentifier({ max: 200 }).nullable();
/** At least one event; duplicates collapse. */
const eventTypes = z
  .array(OperatorWebhookEventTypeSchema)
  .min(1, 'Choose at least one event.')
  .transform((s) => [...new Set(s)]);

/** POST /v1/operator-portal/webhooks. No eventTypes = every event. */
export const CreateOperatorWebhookRequest = z
  .object({
    url,
    description: description.optional(),
    eventTypes: eventTypes.optional(),
  })
  .strict();
export type CreateOperatorWebhookRequest = z.infer<typeof CreateOperatorWebhookRequest>;

/** PATCH /v1/operator-portal/webhooks/:id. Only what is given changes. */
export const UpdateOperatorWebhookRequest = z
  .object({
    url: url.optional(),
    description: description.optional(),
    eventTypes: eventTypes.optional(),
    /** false pauses deliveries to this endpoint; true resumes them. */
    active: z.boolean().optional(),
  })
  .strict()
  .refine((b) => Object.keys(b).length > 0, { message: 'nothing_to_change' });
export type UpdateOperatorWebhookRequest = z.infer<typeof UpdateOperatorWebhookRequest>;

/** POST /v1/operator-portal/webhooks/:id/rotate. 0 = the old secret stops at once. */
export const RotateOperatorWebhookSecretRequest = z
  .object({
    overlapHours: z
      .number()
      .int()
      .min(0)
      .max(OPERATOR_WEBHOOK_ROTATION_OVERLAP_HOURS_MAX)
      .default(OPERATOR_WEBHOOK_ROTATION_OVERLAP_HOURS_DEFAULT),
  })
  .strict();
export type RotateOperatorWebhookSecretRequest = z.infer<typeof RotateOperatorWebhookSecretRequest>;

/** One endpoint as the portal shows it. Never the secret: only its first characters. */
export interface OperatorWebhookEndpointView {
  readonly id: string;
  readonly url: string;
  readonly description: string | null;
  readonly eventTypes: readonly OperatorWebhookEventType[];
  readonly active: boolean;
  /** `whsec_` plus the first characters of the current secret, to tell secrets apart. */
  readonly secretPrefix: string;
  /** Set while a rotated-out secret still signs (dual signatures), else null. */
  readonly previousSecretExpiresAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** GET /v1/operator-portal/webhooks. */
export interface OperatorWebhookListResponse {
  readonly endpoints: readonly OperatorWebhookEndpointView[];
  /** OP_WEBHOOKS on this company's plan. Off: the list still loads; adding is refused. */
  readonly webhooksEnabled: boolean;
  /** OP_WEBHOOKS's limit (the number of endpoints); null = no limit. */
  readonly limit: number | null;
}

/** POST create and POST rotate: the secret, in this response only. */
export interface OperatorWebhookSecretResponse {
  readonly endpoint: OperatorWebhookEndpointView;
  readonly secret: string;
  readonly secretShownOnce: true;
}
